import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Flex } from '@theme-ui/components';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Button } from '@app/components/Button';
import Triangle from '@app/components/Triangle';
import theme from '@app/styles/theme';
import { screenSmall } from '@app/styles/theme/breakpoints';
import { rem } from '@app/styles/theme/layout';

import type {
  SetBillingAddress,
  SetBillingAddressVariables,
} from '../__generated__/SetBillingAddress';
import {
  ADDRESS_FIELD_KEYS,
  AddressFormFields,
  AddressFormNamespaces,
  FORM_REVALIDATE_MODE,
} from '../Address.constants';
import messages from '../Address.messages';
import { SET_BILLING_ADDRESS } from '../Address.queries';
import type { AddressFormState, AddressType } from '../Address.types';
import AddressForm from '../AddressForm/AddressForm';
import { mapToNewCustomerAddress } from '../helpers';
import type { UpdateCustomerAddressMutation } from './__generated__/UpdateCustomerAddressMutation';
import { UPDATE_ADDRESS } from './CustomerAddresses.queries';
import { setBackendValidationError } from './helpers';

const EditAddressForm: FC<{
  cart: { cartId: string; isVirtual?: boolean };
  address: AddressType;
  onClose: () => void;
  selected?: boolean;
  validateOnOpen?: boolean;
}> = ({ onClose, address, cart, selected, validateOnOpen }) => {
  const form = useForm<AddressFormState>({
    reValidateMode: FORM_REVALIDATE_MODE,
  });
  const { setValue, getValues, setError, handleSubmit } = form;
  const { cartId, isVirtual } = cart;

  const hiddenFields = (isVirtual && [AddressFormFields.Phone]) || [];

  const [updateCustomerAddress, { loading: isSendingCustomerAddress }] =
    useMutation<UpdateCustomerAddressMutation>(UPDATE_ADDRESS, {
      onError: (error) => {
        setBackendValidationError(AddressFormNamespaces.Edit, error, setError);
        throw new Error(error.message);
      },
    });

  const [setBillingAddress, { loading: isSendingBillingAddress }] = useMutation<
    SetBillingAddress,
    SetBillingAddressVariables
  >(SET_BILLING_ADDRESS);

  const isLoading = isSendingBillingAddress || isSendingCustomerAddress;

  const submitCustomerAddress = useCallback(
    async (formData) => {
      await updateCustomerAddress({
        variables: {
          id: address.id,
          input: mapToNewCustomerAddress(formData[AddressFormNamespaces.Edit]),
        },
      });
      if (selected && address.id) {
        await setBillingAddress({
          variables: { cartId, billingAddressId: address.id },
        });
      }

      onClose();
    },
    [
      updateCustomerAddress,
      setBillingAddress,
      address.id,
      selected,
      onClose,
      cartId,
    ],
  );

  useEffect(() => {
    // Initialise address into address form
    ADDRESS_FIELD_KEYS.forEach((fieldKey) => {
      const field =
        `${AddressFormNamespaces.Edit}.${fieldKey}` as keyof AddressFormState;
      const value = get(address, fieldKey);
      if (value) {
        setValue(field, value);
      }
    });

    if (validateOnOpen && !isEmpty(getValues())) {
      handleSubmit(submitCustomerAddress)();
    }
  }, [
    setValue,
    getValues,
    address,
    handleSubmit,
    submitCustomerAddress,
    validateOnOpen,
  ]);

  return (
    <Wrapper data-testid="edit-address-form-modal">
      <FormProvider {...form}>
        <AddressForm
          namespace={AddressFormNamespaces.Edit}
          hiddenFields={hiddenFields}
          isVirtual={isVirtual}
        />
        <Flex sx={{ justifyContent: 'space-between' }} mt={10}>
          <Button
            data-testid="cancel-editing-button"
            onClick={onClose}
            type="transparent"
            css={css`
              padding-left: 0.25rem;
            `}
          >
            <Triangle
              css={css`
                margin: 0 0.25rem 0.1rem 0;
              `}
            />
            <FormattedMessage {...messages.cancelEditingButtonLabel} />
          </Button>
          <Button
            type="primary"
            data-testid="save-button"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={handleSubmit(submitCustomerAddress)}
          >
            <FormattedMessage {...messages.saveButtonLabel} />
          </Button>
        </Flex>
      </FormProvider>
    </Wrapper>
  );
};

export default EditAddressForm;

const Wrapper = styled.div`
  background: ${theme.colors.blueishGray};
  padding: ${rem(20)};
  border-radius: 2px;
  width: 30%;

  @media screen and (max-width: ${screenSmall}) {
    width: 100%;
    margin-top: ${rem(80)};
  }
`;
